// src/pages/User/Achievements.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import Sidebar from "../../components/Sidebar";
import Header from "../../components/Header";
import { useAuth } from "../../contexts/AuthContext";
import AddFriendModal from "../../components/User/AddFriendModal";
import AchievementsSkeleton from "../../components/Achievements/AchievementsSkeleton";
import DailyResetTimer from "../../components/Achievements/DailyResetTimer";
import RankStep from "../../components/Achievements/RankStep";
import QuestItem from "../../components/Achievements/QuestItem";
import Trophy from "../../components/Achievements/Trophy";
import {
  QUEST_THEME,
  RANK_STEPS,
  CUSTOM_STYLES,
  normalizeDashPayload,
  getObjectId,
  isCurrentUser,
} from "../../components/Achievements/achievementsConfig";

import {
  getGamificationDashboard,
  claimGamificationReward,
  getGamificationLeaderboard,
  fetchMe,
  getIncomingFriendRequests,
} from "../../services/api";

const Achievements = () => {
  const { user: authUser } = useAuth();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [user, setUser] = useState({
    _id: "",
    id: "",
    email: "",
    firstName: "Student",
    lastName: "",
    avatar: "",
    friends: [],
  });

  const [dash, setDash] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState(null);
  const [error, setError] = useState("");
  const [incomingCount, setIncomingCount] = useState(0);
  const [leaderboardTab, setLeaderboardTab] = useState("all");

  const toggleSidebar = useCallback(() => setIsSidebarOpen((v) => !v), []);
  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);

  // --- USER SNAPSHOT ---
  useEffect(() => {
    let alive = true;

    const applyUser = (u) => {
      if (!alive || !u) return;

      const next = {
        _id: u._id || "",
        id: u.id || u.userId || "",
        email: u.email || "",
        firstName: u.firstName || "Student",
        lastName: u.lastName || "",
        avatar: u.avatar || "",
        friends: Array.isArray(u.friends) ? u.friends : [],
      };

      setUser(next);

      try {
        const cached = JSON.parse(localStorage.getItem("user") || "{}");
        localStorage.setItem("user", JSON.stringify({ ...cached, ...u, ...next }));
      } catch {
        // ignore
      }
    };

    (async () => {
      if (authUser) {
        applyUser(authUser);
        return;
      }

      try {
        const stored = localStorage.getItem("user");
        if (stored) applyUser(JSON.parse(stored));
      } catch {
        // ignore
      }

      try {
        const meRes = await fetchMe();
        const me = meRes?.user ?? meRes?.data ?? meRes;
        if (me) applyUser(me);
      } catch {
        // ignore
      }
    })();

    return () => {
      alive = false;
    };
  }, [authUser]);

  // --- FETCH DASHBOARD + LEADERBOARD ---
  useEffect(() => {
    if (!authUser) return;

    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError("");

        const [dashRes, lbRes] = await Promise.all([
          getGamificationDashboard(),
          getGamificationLeaderboard(),
        ]);

        if (!alive) return;
        setDash(normalizeDashPayload(dashRes));

        const lbPayload = lbRes?.data ?? lbRes?.users ?? lbRes;
        setLeaderboard(Array.isArray(lbPayload) ? lbPayload : []);
      } catch (e) {
        console.error(e);
        if (alive) setError("Failed to load gamification data. Make sure you are logged in.");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [authUser]);

  // --- Preload incoming request count only ---
  useEffect(() => {
    if (!authUser) return;
    let alive = true;

    (async () => {
      try {
        const res = await getIncomingFriendRequests();
        const requests = Array.isArray(res) ? res : res?.data?.requests || res?.data || [];
        if (alive) setIncomingCount(requests.length);
      } catch {
        // ignore badge preload failure
      }
    })();

    return () => {
      alive = false;
    };
  }, [authUser]);

  const derived = useMemo(() => {
    const xp = Number(dash?.xp ?? 0);
    const safeXP = Math.max(0, xp);

    const level = Math.floor(safeXP / 100) + 1;
    const currentLevelStartXP = (level - 1) * 100;
    const nextLevelXP = level * 100;

    const inLevelXP = Math.max(0, safeXP - currentLevelStartXP);
    const neededXP = Math.max(0, nextLevelXP - safeXP);
    const percent = Math.min(100, Math.round((inLevelXP / 100) * 100));

    const rankSteps = RANK_STEPS;

    const totalSteps = rankSteps.length;
    const maxRankLevel = rankSteps[totalSteps - 1].max;
    const clampedLevel = Math.max(1, Math.min(level, maxRankLevel));

    const currentStepIndex = Math.max(
      0,
      rankSteps.findIndex((r) => clampedLevel >= r.min && clampedLevel <= r.max)
    );
    const currentStep = rankSteps[currentStepIndex] || rankSteps[totalSteps - 1];
    const currentRank = currentStep;
    const nextRank = rankSteps[currentStepIndex + 1] || currentStep;

    const stepSpan = Math.max(1, currentStep.max - currentStep.min);
    const inStep = Math.max(0, Math.min(stepSpan, clampedLevel - currentStep.min));
    const rankProgressPercent = Math.min(
      100,
      ((currentStepIndex + inStep / stepSpan) / (totalSteps - 1)) * 100
    );
    const rankIndicatorLeft = Math.max(4, Math.min(96, rankProgressPercent));

    return {
      xp: safeXP,
      level,
      rankLevel: clampedLevel,
      nextLevelXP,
      neededXP,
      percent,
      currentRank,
      nextRank,
      rankSteps,
      rankProgressPercent,
      rankIndicatorLeft,
    };
  }, [dash?.xp]);

  const myIdentity = useMemo(() => {
    return {
      id: String(authUser?._id || authUser?.id || user?._id || user?.id || ""),
      email: String(authUser?.email || user?.email || "").toLowerCase(),
    };
  }, [authUser, user]);

  const friendIdsSet = useMemo(() => {
    const combined = [
      ...(Array.isArray(authUser?.friends) ? authUser.friends : []),
      ...(Array.isArray(user?.friends) ? user.friends : []),
    ];

    return new Set(combined.map(getObjectId).filter(Boolean));
  }, [authUser?.friends, user?.friends]);

  const leaderboardView = useMemo(() => {
    const raw = Array.isArray(leaderboard) ? leaderboard : [];

    const sorted = [...raw].sort((a, b) => Number(b?.xp || 0) - Number(a?.xp || 0));

    const globalRanked = sorted.map((u, i) => ({
      ...u,
      __allRank: i + 1,
    }));

    const isFriendEntry = (u) => {
      const uid = getObjectId(u);
      return u?.isFriend || u?.friendStatus === "accepted" || friendIdsSet.has(uid);
    };

    const meGlobalEntry = globalRanked.find((u) => isCurrentUser(u, myIdentity)) || null;
    const meGlobalRank = meGlobalEntry?.__allRank ?? null;

    const allOthers = globalRanked
      .filter((u) => !isCurrentUser(u, myIdentity))
      .map((u) => ({
        ...u,
        __displayRank: u.__allRank,
      }));

    const friendPool = globalRanked
      .filter((u) => isCurrentUser(u, myIdentity) || isFriendEntry(u))
      .sort((a, b) => Number(b?.xp || 0) - Number(a?.xp || 0))
      .map((u, i) => ({
        ...u,
        __friendRank: i + 1,
      }));

    const meFriendEntry = friendPool.find((u) => isCurrentUser(u, myIdentity)) || null;
    const meFriendRank = meFriendEntry?.__friendRank ?? null;

    const friendOthers = friendPool
      .filter((u) => !isCurrentUser(u, myIdentity))
      .map((u) => ({
        ...u,
        __displayRank: u.__friendRank,
      }));

    return {
      meGlobalEntry,
      meGlobalRank,
      meFriendEntry,
      meFriendRank,
      allOthers,
      friendOthers,
    };
  }, [leaderboard, myIdentity, friendIdsSet]);

  const visibleLeaderboard = useMemo(() => {
    return leaderboardTab === "friends"
      ? leaderboardView.friendOthers
      : leaderboardView.allOthers;
  }, [leaderboardTab, leaderboardView]);

  const currentMeRank = useMemo(() => {
    return leaderboardTab === "friends"
      ? leaderboardView.meFriendRank
      : leaderboardView.meGlobalRank;
  }, [leaderboardTab, leaderboardView]);

  const sortedQuests = useMemo(() => {
    if (!dash?.quests) return [];

    return [...dash.quests].sort((a, b) => {
      const priority = {
        claimable: 1,
        progress: 2,
        completed: 3,
      };

      const pA = priority[a.status] || 2;
      const pB = priority[b.status] || 2;

      if (pA !== pB) return pA - pB;

      if (a.status === "progress" && b.status === "progress") {
        const percentA = Number(a.percentage || 0);
        const percentB = Number(b.percentage || 0);
        return percentB - percentA;
      }

      return 0;
    });
  }, [dash?.quests]);

  const sortedTrophies = useMemo(() => {
    const source = Array.isArray(dash?.trophies) ? dash.trophies : [];

    return [...source].sort((a, b) => {
      const priority = { completed: 1, claimable: 2, progress: 3, locked: 4 };
      const pA = priority[a.status] || 2;
      const pB = priority[b.status] || 2;
      if (pA !== pB) return pA - pB;

      if (a.status === "progress" && b.status === "progress") {
        const percentA = Number(a.percentage || 0);
        const percentB = Number(b.percentage || 0);
        return percentB - percentA;
      }

      return 0;
    });
  }, [dash?.trophies]);

  const unlockedTrophyCount = useMemo(() => {
    if (sortedTrophies.length > 0) {
      return sortedTrophies.filter((t) => t?.status === "completed" || t?.isUnlocked).length;
    }
    return Array.isArray(dash?.badges) ? dash.badges.length : 0;
  }, [sortedTrophies, dash?.badges]);

  const getMyAvatarUrl = () => {
    const url =
      authUser?.avatar ||
      leaderboardView.meGlobalEntry?.avatar ||
      user?.avatar ||
      "";
    if (url) return url;

    const seed = encodeURIComponent(authUser?.firstName || user?.firstName || "Student");
    return `https://api.dicebear.com/9.x/adventurer/svg?seed=${seed}&backgroundColor=b6e3f4`;
  };

  const refreshLeaderboard = useCallback(async () => {
    try {
      const lbRes = await getGamificationLeaderboard();
      const lbPayload = lbRes?.data ?? lbRes?.users ?? lbRes;
      setLeaderboard(Array.isArray(lbPayload) ? lbPayload : []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const handleFriendAccepted = useCallback(
    async (acceptedUser) => {
      const acceptedId = getObjectId(acceptedUser);

      if (acceptedId) {
        setUser((prev) => {
          const prevFriends = Array.isArray(prev.friends) ? prev.friends : [];
          const alreadyExists = prevFriends.some((f) => getObjectId(f) === acceptedId);

          if (alreadyExists) return prev;

          return {
            ...prev,
            friends: [...prevFriends, acceptedId],
          };
        });
      }

      await refreshLeaderboard();
    },
    [refreshLeaderboard]
  );

  const onClaim = useCallback(async (questId) => {
    try {
      setClaimingId(questId);
      await claimGamificationReward(questId);

      const dashRes = await getGamificationDashboard();
      setDash(normalizeDashPayload(dashRes));
    } catch (e) {
      console.error(e);
      alert("Claim failed. Make sure the quest is completed and not already claimed.");
    } finally {
      setClaimingId(null);
    }
  }, []);

  return (
    <div className="bg-slate-50 text-slate-800 font-sans antialiased flex h-screen overflow-hidden">
      <style>{CUSTOM_STYLES}</style>

      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <main className="flex-1 flex flex-col h-full overflow-hidden relative">
        <Header toggleSidebar={toggleSidebar} title="Achievement Zone" />

        <div className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-7xl mx-auto w-full space-y-8 pb-20 md:pb-10">
            {loading && <AchievementsSkeleton />}

            {!loading && error && (
              <div className="bg-white border border-red-100 rounded-2xl p-6 shadow-sm">
                <p className="text-sm text-red-600 font-bold">{error}</p>
              </div>
            )}

            {!loading && !error && dash && (
              <>
                <section className="bg-gradient-to-r from-indigo-700 to-indigo-600 rounded-3xl p-8 text-white relative overflow-hidden shadow-xl shadow-indigo-900/20 h-auto flex flex-col justify-center">
                  <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-5 rounded-full -mr-20 -mt-20 blur-3xl pointer-events-none"></div>
                  <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-400 opacity-10 rounded-full -ml-10 -mb-10 blur-3xl pointer-events-none"></div>

                  <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
                    <div className="relative group">
                      <div className="w-24 h-24 rounded-full border-4 border-white/20 flex items-center justify-center bg-white/10 backdrop-blur-md shadow-2xl relative overflow-hidden">
                        <img src={getMyAvatarUrl()} alt="Avatar" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition duration-500"></div>
                      </div>

                      <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-yellow-400 text-indigo-900 text-[10px] font-bold px-3 py-0.5 rounded-full border border-indigo-800 shadow-lg whitespace-nowrap">
                        Lvl {derived.level}
                      </div>
                    </div>

                    <div className="flex-1 w-full">
                      <div className="flex justify-between items-end mb-2">
                        <div>
                          <h1 className="text-2xl font-heading font-bold">{derived.currentRank.label}</h1>
                          <p className="text-indigo-100 text-xs md:text-sm">
                            Next Rank:{" "}
                            <span className="font-bold text-white">{derived.nextRank.label}</span>{" "}
                            (Level {derived.nextRank.min})
                          </p>
                        </div>

                        <div className="text-right">
                          <span className="text-xl font-bold font-heading">{derived.xp}</span>{" "}
                          <span className="text-indigo-200 text-xs">/ {derived.nextLevelXP} XP</span>
                        </div>
                      </div>

                      <div className="w-full h-3 bg-black/20 rounded-full overflow-hidden backdrop-blur-sm relative border border-white/5">
                        <div
                          className={`h-full rounded-full relative overflow-hidden ${derived.currentRank.xpBarClass}`}
                          style={{ width: `${derived.percent}%` }}
                        >
                          <div className="absolute inset-0 w-full h-full progress-bar-shine"></div>
                        </div>
                      </div>

                      <p className="text-[10px] text-indigo-200 mt-2 text-right">
                        {derived.neededXP} XP needed to level up
                      </p>
                    </div>
                  </div>
                </section>

                <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
                  <h3 className="text-sm font-heading font-bold text-gray-800 mb-6 flex items-center gap-2">
                    <i className="fa-solid fa-layer-group text-indigo-500"></i> Rank Progression
                  </h3>

                  <div className="relative">
                    <div className="absolute top-1/2 left-0 w-full h-1 bg-gray-100 -translate-y-1/2 rounded-full z-0"></div>
                    <div
                      className={`absolute top-1/2 left-0 h-1 -translate-y-1/2 rounded-full z-0 transition-all duration-500 ${derived.currentRank.lineClass}`}
                      style={{ width: `${derived.rankProgressPercent}%` }}
                    ></div>
                    <div
                      className="absolute top-1/2 z-20 -translate-y-1/2 -translate-x-1/2 transition-all duration-500"
                      style={{ left: `${derived.rankIndicatorLeft}%` }}
                    >
                      <span
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold shadow-sm ${derived.currentRank.indicatorClass}`}
                      >
                        <i className={`fa-solid ${derived.currentRank.icon}`}></i>
                        {derived.currentRank.label}
                      </span>
                    </div>

                    <div className="relative z-10 flex justify-between items-start">
                      {derived.rankSteps.map((step) => {
                        const status =
                          derived.rankLevel > step.max
                            ? "completed"
                            : derived.rankLevel >= step.min && derived.rankLevel <= step.max
                            ? "current"
                            : "locked";

                        return (
                          <RankStep
                            key={step.label}
                            icon={step.icon}
                            label={step.label}
                            sub={step.sub}
                            status={status}
                            tone={step.stepClasses}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="text-lg font-heading font-bold text-gray-800 flex items-center gap-2">
                          <i className="fa-solid fa-scroll text-indigo-500"></i> Daily Quests
                        </h3>
                        <DailyResetTimer />
                      </div>

                      <div className="space-y-3">
                        {sortedQuests.map((q) => {
                          const color = QUEST_THEME[q.id] || "indigo";
                          return (
                            <QuestItem
                              key={q.id}
                              quest={q}
                              color={color}
                              onClaim={onClaim}
                              claiming={claimingId === q.id}
                            />
                          );
                        })}
                      </div>
                    </div>

                    <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="text-lg font-heading font-bold text-gray-800 flex items-center gap-2">
                          <i className="fa-solid fa-award text-yellow-500"></i> Trophy Room
                        </h3>
                      </div>

                      <div className="text-xs text-gray-500 mb-4">
                        Unlocked trophies: <span className="font-bold">{unlockedTrophyCount}</span>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {sortedTrophies.map((t, idx) => (
                          <Trophy
                            key={t.id}
                            icon={t.icon || "fa-award"}
                            label={t.title || String(t.id || "").replaceAll("_", " ")}
                            description={
                              t.description || t.criteria || t.requirement || t.howToEarn || ""
                            }
                            color={t.color || "yellow"}
                            delay={`${0.1 * (idx + 1)}s`}
                            status={t.status}
                            progress={Number(t.progress || 0)}
                            target={Number(t.target || 0)}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm sticky top-6">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-sm font-heading font-bold text-gray-800">Leaderboard</h3>

                        <button
                          onClick={openModal}
                          className="text-xs bg-indigo-50 text-indigo-600 hover:bg-indigo-100 font-bold px-3 py-1.5 rounded-lg transition flex items-center gap-1"
                        >
                          <i className="fa-solid fa-user-plus"></i> Add
                          {incomingCount > 0 && (
                            <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                              {incomingCount}
                            </span>
                          )}
                        </button>
                      </div>

                      <div className="flex bg-gray-100 rounded-xl p-1 mb-4">
                        <button
                          onClick={() => setLeaderboardTab("all")}
                          className={`flex-1 py-2 rounded-lg text-xs font-bold transition ${
                            leaderboardTab === "all"
                              ? "bg-white text-indigo-600 shadow-sm"
                              : "text-gray-500"
                          }`}
                        >
                          All
                        </button>

                        <button
                          onClick={() => setLeaderboardTab("friends")}
                          className={`flex-1 py-2 rounded-lg text-xs font-bold transition ${
                            leaderboardTab === "friends"
                              ? "bg-white text-indigo-600 shadow-sm"
                              : "text-gray-500"
                          }`}
                        >
                          Friends
                        </button>
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-center gap-3 bg-gradient-to-r from-orange-50 to-white p-3 rounded-xl -mx-2 border border-orange-100 shadow-[0_0_20px_rgba(249,115,22,0.15)] transform scale-105 transition-all relative">
                          <span className="w-8 text-center text-sm font-bold text-orange-500">
                            {currentMeRank === 1 ? (
                              <i className="fa-solid fa-crown"></i>
                            ) : (
                              currentMeRank ?? "-"
                            )}
                          </span>

                          <div className="relative w-10 h-10 flex justify-center items-center">
                            <img
                              src={getMyAvatarUrl()}
                              alt="Me"
                              className="w-10 h-10 rounded-full shadow-md border-2 border-white bg-orange-100 object-cover"
                            />
                          </div>

                          <div className="flex-1">
                            <p className="text-sm font-bold text-gray-900">You</p>
                            <p className="text-[10px] text-orange-600 font-bold flex items-center gap-1">
                              {derived.xp} XP <i className="fa-solid fa-arrow-trend-up text-[8px]"></i>
                            </p>
                          </div>
                        </div>

                        <div className="text-[11px] text-gray-500 font-medium">
                          Showing{" "}
                          <span className="font-bold text-gray-700">
                            {leaderboardTab === "friends" ? "Friends Ranking" : "All Users"}
                          </span>
                        </div>

                        {visibleLeaderboard.length === 0 ? (
                          <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-4 text-center">
                            <p className="text-sm font-semibold text-gray-700">
                              {leaderboardTab === "friends" ? "No friends yet" : "No leaderboard data"}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {leaderboardTab === "friends"
                                ? "Add friends to compare your XP with them."
                                : "Leaderboard entries will appear here."}
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                            {visibleLeaderboard.map((u) => (
                              <div
                                key={
                                  u._id || u.id || u.userId || `${leaderboardTab}-rank-${u.__displayRank}`
                                }
                                className="flex items-center gap-3 p-2 rounded-xl hover:bg-gray-50 transition"
                              >
                                <span className="w-8 text-center text-sm font-bold text-gray-400">
                                  {u.__displayRank}
                                </span>

                                <img
                                  src={
                                    u.avatar ||
                                    `https://api.dicebear.com/9.x/adventurer/svg?seed=${encodeURIComponent(
                                      u.firstName || "User"
                                    )}&backgroundColor=b6e3f4`
                                  }
                                  alt={u.firstName}
                                  className="w-9 h-9 rounded-full bg-gray-100 border border-gray-200 object-cover"
                                />

                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-bold text-gray-800 truncate">
                                    {u.firstName} {u.lastName}
                                  </p>
                                  <p className="text-[10px] text-gray-500">{Number(u.xp || 0)} XP</p>
                                </div>

                                {leaderboardTab === "friends" && (
                                  <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-indigo-50 text-indigo-600">
                                    Friend
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      <AddFriendModal
        isOpen={isModalOpen}
        onClose={closeModal}
        myIdentity={myIdentity}
        incomingCount={incomingCount}
        setIncomingCount={setIncomingCount}
        onFriendAccepted={handleFriendAccepted}
      />
    </div>
  );
};

export default Achievements;
